// src/pages/GraphPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import client from "../api_v2/client";
import { Spin, Button } from "antd";
import { Maximize2 } from "lucide-react";
import NoteModal from "./NoteModal.tsx";

/* ──────── Типы ──────── */
interface NoteItem {
  id?: number;
  itemText: string;
  checked: boolean;
  position?: number;
}

interface Label {
  id: number;
  name: string;
  color?: string;
}

interface Note {
  id: number;
  user_id: number;
  title: string | null;
  body:  string | null;
  is_list: boolean;
  color?:       number;
  color_argb?:  number;
  pinned?:      boolean;
  deleted?:     boolean;
  rev?:         number;
  items?:  NoteItem[];
  labels?: Label[];
}

interface LabelMapItem {
  note_id:  number;
  label_id: number;
  label_name: string;
}

/* ──────── regex-утилиты ──────── */
const HASH_RE = /(^|\s)#([\p{L}\p{N}_]+)/gu;
const AT_RE   = /(^|\s)@([\p{L}\p{N}_ ][^\n]*)/gu;
const extract = (re: RegExp, txt: string | null) =>
  txt ? Array.from(txt.matchAll(re), m => m[2].trim()) : [];

const argbToHex = (argb: number) =>
  `#${(argb >>> 0).toString(16).padStart(8, "0").slice(2)}`;

const mixWithWhite = (hex: string) => {
  const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16));
  const mix = (c: number) => Math.round((c + 255) / 2)
    .toString(16).padStart(2, "0");
  return `#${mix(r)}${mix(g)}${mix(b)}`;
};

/* ──────── Компонент ──────── */
export default function GraphPage() {
  const userId =
    JSON.parse(localStorage.getItem("simolapps_state") || "{}").user?.id;

  const [notes,       setNotes]       = useState<Note[] | null>(null);
  const [noteLabels,  setNoteLabels]  = useState<LabelMapItem[] | null>(null);
  const [selected,    setSelected]    = useState<Note | null>(null);

  /* ссылка на экземпляр cytoscape */
  const cyRef = useRef<cytoscape.Core | null>(null);

  /* ─── загрузка ─── */
  useEffect(() => {
    if (!userId) return;
    client
      .get("/ruznama/get_all.php", { params: { user_id: userId } })
      .then(r => r.data.success && setNotes(r.data.result))
      .catch(console.error);

    client
      .get("/ruznama/get_note_labels.php", { params: { user_id: userId } })
      .then(r => r.data.success && setNoteLabels(r.data.result))
      .catch(console.error);
  }, [userId]);

  /* ─── построение элементов ─── */
  const { elements, layout } = useMemo(() => {
    if (!notes) return { elements: [], layout: { name: "grid" } };

    const nodes: Record<string, any> = {};
    const edges: Record<string, any> = {};

    const ensure = (
      id: string,
      label: string | null,
      type: "note" | "tag" | "label",
      color?: string
    ) => {
      if (!nodes[id])
        nodes[id] = {
          data: { id, label: label ?? "(no title)", type, count: 1, color },
        };
      else nodes[id].data.count += 1;
    };

    const titleMap = new Map(
      notes.filter(n => n.title).map(n => [n.title!.trim(), n])
    );

    notes.forEach(note => {
      const nid = `note_${note.id}`;
      ensure(
        nid,
        note.title,
        "note",
        note.color_argb ? argbToHex(note.color_argb) : undefined
      );

      [...extract(HASH_RE, note.title), ...extract(HASH_RE, note.body)].forEach(
        tag => {
          const tid = `tag_${tag}`;
          ensure(tid, `#${tag}`, "tag");
          edges[`${nid}-${tid}`] = {
            data: { id: `${nid}-${tid}`, source: nid, target: tid },
          };
        }
      );

 extract(AT_RE, note.body).forEach((title) => {
  const t = titleMap.get(title);
   if (!t) return;
   const tid = `note_${t.id}`;
   edges[`${nid}-${tid}`] = {
     data: { id: `${nid}-${tid}`, source: nid, target: tid },
   };
 });
    });

    noteLabels?.forEach(l => {
      const nid = `note_${l.note_id}`;
      if (!nodes[nid]) return;
      const lid = `label_${l.label_id}`;
      ensure(lid, l.label_name, "label");
      edges[`${nid}-${lid}`] = {
        data: { id: `${nid}-${lid}`, source: nid, target: lid },
      };
    });

    return {
      elements: [...Object.values(nodes), ...Object.values(edges)],
      layout: {
        name: "concentric",
        padding: 20,
        animate: true,
        avoidOverlap: true,
        spacingFactor: 1.2,
        concentric: n => n.data("count") || 1,
        levelWidth: ns =>
          Math.max(...ns.map(n => n.data("count") || 1)) / 6,
      },
    };
  }, [notes, noteLabels]);

  if (!notes || !noteLabels) return <Spin style={{ color: "#fff" }} />;

  /* ─── UI ─── */
  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        background: "#000",
        color: "#fff",
        padding: 16,
        position: "relative",
      }}
    >
      <h2 style={{ margin: 0, marginBottom: 12 }}>Паутина мыслей</h2>

      {/* Кнопка «Нормальный размер» */}
      <Button
        type="primary"
        shape="circle"
        size="small"
        icon={<Maximize2 size={14} />}
        style={{ position: "absolute", right: 16, top: 12, zIndex: 10 }}
onClick={()=>{
  if (!cyRef.current) return;
  cyRef.current.fit(undefined, 50); // padding 50px
  console.log("🔄 Fit graph");
}}

      />

      <CytoscapeComponent
        elements={elements}
        layout={layout}
        stylesheet={graphStylesheet}
        style={{ width: "100%", height: "calc(100% - 40px)" }}
        cy={cy => {
          cyRef.current = cy;

          /* Логируем zoom один раз */
          if (!(cy as any)._zoomLogger) {
            cy.on("zoom", () =>
              console.log("🔍 Zoom:", cy.zoom().toFixed(2))
            );
            (cy as any)._zoomLogger = true;
          }

          /* Окраска нод */
cy.nodes('[type = "note"]').forEach(n => {
  const col = n.data("color");
  if (col) n.style("background-color", col);
});



          /* Начальный zoom */
          if (!(cy as any)._initialDone) {
            cy.zoom(0.5);
            cy.center();
            (cy as any)._initialDone = true;
          }

          /* Hover-подсветка */
          cy.on("mouseover", "node", e => {
            const id = e.target.id();
            cy.elements().removeClass("highlighted");
            e.target.addClass("highlighted");
            cy.$(`edge[source = "${id}"], edge[target = "${id}"]`)
              .addClass("highlighted")
              .connectedNodes()
              .addClass("highlighted");
          });
          cy.on("mouseout", "node", () =>
            cy.elements().removeClass("highlighted")
          );

          /* Tap → открыть заметку */
          cy.on("tap", "node[type = 'note']", e => {
            const rawId = e.target.id().replace("note_", "");
            const note = notes.find(n => String(n.id) === rawId);
            if (note) setSelected(note);
          });
        }}
      />

      {/* ─── модальное окно редактирования ─── */}
      <NoteModal
        open={!!selected}
        note={
          selected ?? {
            id: 0,
            user_id: userId,
            title: "",
            body: "",
            is_list: false,
            items: [],
            labels: [],
          }
        }
        onCancel={() => setSelected(null)}
        onSave={upd => {
          setNotes(prev => prev!.map(n => (n.id === upd.id ? upd : n)));
          setSelected(null);
        }}
        onDelete={() => {
          if (!selected) return;
          setNotes(prev => prev!.filter(n => n.id !== selected.id));
          setSelected(null);
        }}
      />
    </div>
  );
}

/* ──────── Стили Cytoscape ──────── */
const graphStylesheet = [
  {
  
    selector: 'node[type="note"]',
    style: {
      shape: "ellipse",
      width:  "mapData(count, 1, 10, 10, 18)",
      height: "mapData(count, 1, 10, 10, 18)",
      backgroundColor: (ele: any) => {
        const raw = ele.data("color");
        return raw ? mixWithWhite(raw) : "#1e90ff";
      },
      label: "data(label)",
      color: "#fff",
      textValign: "center",
      textHalign: "right",
      textMarginX: 1,
      textMarginY: -1,
      fontSize: 3,
    },
  },
  {
    selector: 'node[type="tag"]',
    style: {
      shape: "diamond",
      width: 8,
      height: 8,
      backgroundColor: "#d5d5d5",
      label: "data(label)",
      color: "#fff",
      textValign: "center",
      textHalign: "right",
      textMarginX: 1,
      textMarginY: -1,
      fontSize: 3,
    },
  },
  {
    selector: 'node[type="label"]',
    style: {
      shape: "rectangle",
      width: 8,
      height: 8,
      backgroundColor: "#d5d5d5",
      label: "data(label)",
      color: "#fff",
      textValign: "center",
      textHalign: "right",
      textMarginX: 1,
      textMarginY: -1,
      fontSize: 3,
    },
  },
  {
    selector: "edge",
    style: { width: 0.5, lineColor: "#666", curveStyle: "bezier" },
  },
  {
    selector: "edge.highlighted",
    style: { lineColor: "#fff", width: 0.5 },
  },
  {
    selector: "node.highlighted",
    style: { borderColor: "#fff", borderWidth: 0.5 },
  },
];
